/**
 * VolumetricFogPass — analytic volumetric fog as a depth-aware post pass:
 * closed-form exponential height fog (Quilez), Henyey-Greenstein-style sun
 * in-scatter tinting, and up to MAX_FOG_VOLUMES analytic local box volumes
 * (uniform density × ray/AABB overlap — the UE "Local Fog Volume" fast
 * analytic integral).
 *
 * Replaces the old `render/gi/VolumetricFog.ts` stub with a REAL effect that
 * runs identically on WebGL2 and WebGPU (no compute, no froxel grid — the
 * light-injected froxel version is the documented v2 on top of this pass).
 *
 * Math (height fog, density d(y) = a·e^(−b·y), ray o + t·rd):
 *   opticalDepth = (a/b)·e^(−b·o_y)·(1 − e^(−b·rd_y·t))/rd_y     (rd_y ≠ 0)
 *                =  a·e^(−b·o_y)·t                                (rd_y → 0)
 *   transmittance = e^(−opticalDepth − Σ volumeOD)
 *   colour        = scene·T + fogColour·(1 − T)
 * with fogColour tinted toward the sun by pow(max(rd·sun, 0), sunPower).
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, exp, pow, mix, min, max,
  saturate, normalize, abs, select, perspectiveDepthToViewZ,
  cameraPosition, cameraWorldMatrix,
} from 'three/tsl';

export const MAX_FOG_VOLUMES = 4;

export interface VolumetricFogPassConfig {
  /** Base fog colour (default 0.65, 0.72, 0.82). */
  color?: [number, number, number];
  /** Density at height 0 (default 0.015). */
  density?: number;
  /** Height falloff b — larger = thinner with altitude (default 0.06). */
  heightFalloff?: number;
  /** World height where density is measured from (default 0). */
  baseHeight?: number;
  /** Sun tint colour (default 1, 0.9, 0.7). */
  sunColor?: [number, number, number];
  /** Sun direction, world (default up-ish noon). */
  sunDirection?: [number, number, number];
  /** Sun forward-scatter exponent (default 8). */
  sunPower?: number;
  /** Start distance — fog-free near field (default 0). */
  startDistance?: number;
  /** Max fog opacity 0..1 (default 1). */
  maxOpacity?: number;
  enabled?: boolean;
}

export interface FogVolumeDef {
  center: [number, number, number];
  halfExtents: [number, number, number];
  density: number;
}

export interface VolumetricFogUniforms {
  color: { value: THREE.Vector3 };
  density: { value: number };
  heightFalloff: { value: number };
  baseHeight: { value: number };
  sunColor: { value: THREE.Vector3 };
  sunDirection: { value: THREE.Vector3 };
  sunPower: { value: number };
  startDistance: { value: number };
  maxOpacity: { value: number };
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  /** Per-volume uniforms (centers/halfExtents as Vector3, density scalar; density 0 = slot off). */
  volumeCenters: Array<{ value: THREE.Vector3 }>;
  volumeHalfExtents: Array<{ value: THREE.Vector3 }>;
  volumeDensities: Array<{ value: number }>;
  /** Convenience: write a slot (null clears). */
  setVolume(index: number, volume: FogVolumeDef | null): void;
}

// ─── CPU mirrors (exported for tests) ───────────────────────────────────────

/** Closed-form exponential height-fog optical depth along a ray. */
export function heightFogOpticalDepthCpu(
  originY: number, rdY: number, dist: number,
  density: number, falloff: number, baseHeight: number,
): number {
  const oy = originY - baseHeight;
  const a = density;
  const b = falloff;
  if (Math.abs(rdY) < 1e-4) {
    return a * Math.exp(-b * oy) * dist;
  }
  return (a / b) * Math.exp(-b * oy) * (1 - Math.exp(-b * rdY * dist)) / rdY;
}

/** Ray/AABB overlap length clipped to [0, maxT]. */
export function rayBoxOverlapCpu(
  o: [number, number, number], rd: [number, number, number],
  center: [number, number, number], half: [number, number, number],
  maxT: number,
): number {
  let t0 = 0;
  let t1 = maxT;
  for (let i = 0; i < 3; i++) {
    const inv = 1 / (rd[i] || 1e-9);
    let tNear = (center[i] - half[i] - o[i]) * inv;
    let tFar = (center[i] + half[i] - o[i]) * inv;
    if (tNear > tFar) { const tmp = tNear; tNear = tFar; tFar = tmp; }
    t0 = Math.max(t0, tNear);
    t1 = Math.min(t1, tFar);
  }
  return Math.max(0, t1 - t0);
}

// ─── Pass factory ───────────────────────────────────────────────────────────

export function createVolumetricFogNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config?: VolumetricFogPassConfig,
): { node: unknown; uniforms: VolumetricFogUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const c = config?.color ?? [0.65, 0.72, 0.82];
  const sc = config?.sunColor ?? [1, 0.9, 0.7];
  const sd = config?.sunDirection ?? [0.3, 0.8, 0.5];

  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uDensity = uniform(config?.density ?? 0.015);
  const uFalloff = uniform(config?.heightFalloff ?? 0.06);
  const uBaseHeight = uniform(config?.baseHeight ?? 0);
  const uSunColor = uniform(vec3(sc[0], sc[1], sc[2]));
  const uSunDir = uniform(vec3(sd[0], sd[1], sd[2]));
  const uSunPower = uniform(config?.sunPower ?? 8);
  const uStart = uniform(config?.startDistance ?? 0);
  const uMaxOpacity = uniform(config?.maxOpacity ?? 1);
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);

  const volumeCenters: Array<ReturnType<typeof uniform>> = [];
  const volumeHalfExtents: Array<ReturnType<typeof uniform>> = [];
  const volumeDensities: Array<ReturnType<typeof uniform>> = [];
  for (let i = 0; i < MAX_FOG_VOLUMES; i++) {
    volumeCenters.push(uniform(vec3(0, 0, 0)));
    volumeHalfExtents.push(uniform(vec3(0, 0, 0)));
    volumeDensities.push(uniform(0));
  }

  const build = Fn(() => {
    const cIn = vec4(input as never);
    const depthScalar = ((depthNode as { r?: unknown }).r ?? depthNode) as never;
    const viewZ = perspectiveDepthToViewZ(depthScalar, uNear, uFar);
    const ndc = uv().mul(2).sub(vec2(1, 1));
    const rayView = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(-1));
    const dist = viewZ.negate().mul(rayView.length());
    // World ray via the camera's world matrix rotation (w=0 transform).
    const rdWorld = normalize(
      cameraWorldMatrix.mul(vec4(rayView, 0)).xyz,
    );
    const marched = max(dist.sub(uStart), float(0));

    // Height fog closed form, with the rdY→0 limit handled via select.
    const oy = cameraPosition.y.sub(uBaseHeight);
    const rdY = rdWorld.y;
    const flat = uDensity.mul(exp(uFalloff.negate().mul(oy))).mul(marched);
    const slanted = uDensity.div(uFalloff)
      .mul(exp(uFalloff.negate().mul(oy)))
      .mul(float(1).sub(exp(uFalloff.negate().mul(rdY).mul(marched))))
      .div(rdY);
    const heightOD = select(abs(rdY).lessThan(1e-4), flat, slanted);

    // Local box volumes: uniform density × ray overlap length.
    let volumeOD: ReturnType<typeof float> = float(0);
    for (let i = 0; i < MAX_FOG_VOLUMES; i++) {
      const bmin = (volumeCenters[i] as never as ReturnType<typeof vec3>).sub(volumeHalfExtents[i] as never);
      const bmax = (volumeCenters[i] as never as ReturnType<typeof vec3>).add(volumeHalfExtents[i] as never);
      const invD = vec3(1, 1, 1).div(rdWorld.add(vec3(1e-9, 1e-9, 1e-9)));
      const tA = bmin.sub(cameraPosition).mul(invD);
      const tB = bmax.sub(cameraPosition).mul(invD);
      const tMin = min(tA, tB);
      const tMax = max(tA, tB);
      const tEnter = max(max(tMin.x, tMin.y), max(tMin.z, float(0)));
      const tExit = min(min(tMax.x, tMax.y), min(tMax.z, dist));
      const overlap = max(tExit.sub(tEnter), float(0));
      volumeOD = volumeOD.add(overlap.mul(volumeDensities[i] as never)) as ReturnType<typeof float>;
    }

    const trans = exp(heightOD.add(volumeOD).negate());
    const transClamped = mix(float(1), trans, uMaxOpacity);

    // Sun-tinted fog colour (forward-scatter glow toward the sun).
    const sunAmount = pow(saturate(rdWorld.dot(normalize(uSunDir))), uSunPower);
    const fogCol = mix(uColor, uSunColor, sunAmount);

    return vec4(mix(fogCol, cIn.rgb, transClamped), cIn.a);
  });

  const asVec3 = (u: ReturnType<typeof uniform>): { value: THREE.Vector3 } =>
    u as unknown as { value: THREE.Vector3 };
  const asNum = (u: ReturnType<typeof uniform>): { value: number } =>
    u as unknown as { value: number };

  const uniforms: VolumetricFogUniforms = {
    color: asVec3(uColor),
    density: asNum(uDensity),
    heightFalloff: asNum(uFalloff),
    baseHeight: asNum(uBaseHeight),
    sunColor: asVec3(uSunColor),
    sunDirection: asVec3(uSunDir),
    sunPower: asNum(uSunPower),
    startDistance: asNum(uStart),
    maxOpacity: asNum(uMaxOpacity),
    near: asNum(uNear),
    far: asNum(uFar),
    tanHalfFovY: asNum(uTanHalf),
    aspect: asNum(uAspect),
    volumeCenters: volumeCenters.map(asVec3),
    volumeHalfExtents: volumeHalfExtents.map(asVec3),
    volumeDensities: volumeDensities.map(asNum),
    setVolume(index: number, volume: FogVolumeDef | null): void {
      if (index < 0 || index >= MAX_FOG_VOLUMES) return;
      if (!volume) {
        this.volumeDensities[index].value = 0;
        return;
      }
      this.volumeCenters[index].value.set(...volume.center);
      this.volumeHalfExtents[index].value.set(...volume.halfExtents);
      this.volumeDensities[index].value = volume.density;
    },
  };

  return { node: build(), uniforms };
}
